import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import {
  ArrowLeft, Calendar, Briefcase, MapPin, Slash, Edit, AlertTriangle, DollarSign, XCircle, Tool, Shield,
  CheckCircle, Clock,
} from "react-feather";
import toast from "react-hot-toast";
import {
  useGetOrderQuery, useCloseOrderMutation, useCancelOrderMutation, useSetInstallationMutation, useSetOrderDiscountMutation,
} from "../../salesApi";
import CollapsibleCard, { PANEL_ACCENT } from "../../components/CollapsibleCard";
import { AmountTile, ServicePill, StepTrack, GuardRequirementsTable } from "../../components/PanelBits";
import DiscountFields, { DiscountedTotalsStrip, computeDiscountedTotals, discountRequest } from "../../components/DiscountFields";
import { ServiceLinesTable, hasPricedLines } from "../../components/ServiceLinesGrid";
import SalesQuotationsPanel from "../../components/SalesQuotationsPanel";
import SalesLinkedDocumentsPanel from "../../components/SalesLinkedDocumentsPanel";
import { INSTALLATION_STATUS_LABEL, orderStatus } from "../../salesStatusMaps";
import { useCanCloseOrder, useCanManageOperations, useCanManageCommission, useCanApproveEnquiry } from "../../useSalesPermissions";
import CommissionModal from "../../commissions/components/CommissionModal";
import CustomerFeedbackPanel from "../../../serviceFeedback/components/CustomerFeedbackPanel";
import { paths } from "../../../../routes/paths";

const money = (n) => (n == null ? "-" : Number(n).toFixed(2));
const d = (v) => (v ? new Date(v).toLocaleDateString() : "");

// Guard deployment marking is deliberately NOT done from here (or from the
// Enquiry side either) - per direct instruction, that's a separate
// module's job. This screen only ever shows the order's guard requirements
// as read-only (what was requested) plus a plain deployed/pending
// indicator; the Sales Dashboard is where a manager filters/sees deployment
// status across many orders at once.
const OrderDetail = () => {
  const { recordGuid } = useParams();
  const canCloseOrder = useCanCloseOrder();
  const canManageOperations = useCanManageOperations();
  const canManageCommission = useCanManageCommission();
  // Discounting is pricing authority - the same permission that prices an
  // enquiry (Batch147/163).
  const canDiscount = useCanApproveEnquiry();
  const [setOrderDiscount, { isLoading: isSavingDiscount }] = useSetOrderDiscountMutation();
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");

  const { data: detail, isLoading, isError, error } = useGetOrderQuery(recordGuid);
  const [closeOrder, { isLoading: isClosing }] = useCloseOrderMutation();
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();
  const [setInstallation, { isLoading: isSavingInstallation }] = useSetInstallationMutation();

  const [showCloseForm, setShowCloseForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCommission, setShowCommission] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [showInstallForm, setShowInstallForm] = useState(false);
  const [installStatus, setInstallStatus] = useState("SCHEDULED");
  const [scheduledDate, setScheduledDate] = useState("");
  const [startedDate, setStartedDate] = useState("");
  const [expectedDays, setExpectedDays] = useState("");
  const [onHoldDate, setOnHoldDate] = useState("");
  const [onHoldRemarks, setOnHoldRemarks] = useState("");
  const [completedDate, setCompletedDate] = useState("");
  const [handoverNumber, setHandoverNumber] = useState("");
  const [technicianIds, setTechnicianIds] = useState("");

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !detail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.salesOrders} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Orders</Link>
          <div className="alert alert-danger">Could not load this order ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const { order, guardRequirements = [], serviceTypes = [] } = detail;
  // Priced service lines (Batch164, copied from the enquiry at Confirm).
  // Older orders have unpriced lines and keep the per-service tiles.
  const serviceLines = detail.serviceLines ?? [];
  // Who booked the business and for whom (Batch188). `booking` is null on an
  // API that predates it, and the name is null for an account since removed.
  const booking = detail.booking ?? null;
  // The salesperson is an EMPLOYEE, the booker a LOGIN, and the order's
  // SalespersonUserId is just the booking login again (checked on live data) -
  // so ids cannot tell "booked for someone else" apart. Names can: the booker
  // comes back as a first name, so compare first words.
  const firstWord = (v) => (v || "").trim().split(/s+/)[0].toUpperCase();
  const bookedForOther = Boolean(order?.salespersonName) && firstWord(order.salespersonName) !== firstWord(booking?.bookedByName);
  const linesArePriced = hasPricedLines(serviceLines);

  // An order with guards on it cannot be closed until those guards are
  // actually deployed - closing first would leave a signed, paid-for order
  // whose guards nobody is tracking, and the Pending Deployments queue is
  // driven off exactly this flag.
  //
  // UI-ONLY, per direct instruction: the API's CloseOrderAsync has no such
  // check and CloseSalesOrderRequest.Reason is still `string?`. So this gate
  // and the mandatory invoice number below are both enforced here alone -
  // anything calling the endpoint directly (or the Flutter app) is unaffected.
  // Worth remembering if the rule ever needs to be real rather than advisory.
  const isDeploymentPending = order.guardsCount > 0 && !order.isGuardDeployed;

  const handleClose = async () => {
    if (isDeploymentPending) return;
    if (!invoiceNumber.trim()) return toast.error("Invoice number is required to close an order");
    try {
      // Still posted as `reason` - that is the field the endpoint accepts and
      // it lands in ClosedReason. Only the label and the requirement changed.
      await closeOrder({ recordGuid, reason: invoiceNumber.trim() }).unwrap();
      toast.success("Order closed");
      setShowCloseForm(false);
      setInvoiceNumber("");
    } catch (e) {
      toast.error(e?.message || "Could not close the order");
    }
  };

  // Cancel = the customer backed out (Batch162) - not Close, which means
  // delivered and invoiced. Open orders only; the API also closes the order's
  // unfinished technical work. Same Sales.CloseOrder permission as Close.
  const handleCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A reason is required to cancel an order");
    try {
      await cancelOrder({ recordGuid, reason: cancelReason.trim() }).unwrap();
      toast.success("Order cancelled");
      setShowCancelForm(false);
      setCancelReason("");
    } catch (e) {
      toast.error(e?.message || "Could not cancel the order");
    }
  };

  // Pricing breakdown. TotalAmount/VatAmount on the order are already net of
  // the discount; the gross sub total is rebuilt from the priced amounts.
  const orderSubTotal =
    (Number(order.guardsAmount) || 0) + (Number(order.alarmsAmount) || 0)
    + (Number(order.otherServicesAmount) || 0) + (Number(order.tempGuardsAmount) || 0);
  const newTotals = computeDiscountedTotals(orderSubTotal, order.vatRate, discountPercent, discountAmount);
  const canEditDiscount = canDiscount && !order.isClosed;

  const openDiscountForm = () => {
    setDiscountPercent(order.discountPercent ? String(order.discountPercent) : "");
    setDiscountAmount(!order.discountPercent && order.discountAmount ? String(order.discountAmount) : "");
    setShowDiscountForm(true);
  };

  const handleSaveDiscount = async () => {
    if (newTotals.exceeds) return toast.error("The discount is more than the sub total");
    try {
      await setOrderDiscount({ recordGuid, ...discountRequest(discountPercent, discountAmount) }).unwrap();
      toast.success(newTotals.discount > 0 ? "Discount applied" : "Discount removed");
      setShowDiscountForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not update the discount");
    }
  };

  // Order life: Confirmed -> Guards Deployed (guard orders) -> Installation
  // (alarms/other work) -> Closed -> Commission, or ending in Cancelled. The
  // first step not yet done is "current" - unless it is on hold (warn).
  const hasInstallWork = Boolean(order.installationStatus)
    || Number(order.alarmsCount) > 0 || Number(order.otherServicesAmount) > 0;
  const lifeSteps = [{ key: "confirmed", label: "Confirmed", sub: d(order.createdDate), state: "done" }];
  if (order.guardsCount > 0) {
    lifeSteps.push({
      key: "guards", label: "Guards Deployed",
      sub: order.isGuardDeployed ? d(order.guardDeploymentDate) : `${order.guardsCount} guard${order.guardsCount === 1 ? "" : "s"}`,
      state: order.isGuardDeployed ? "done" : "todo",
    });
  }
  if (hasInstallWork) {
    const s = order.installationStatus;
    lifeSteps.push({
      key: "install", label: "Installation",
      sub: s ? INSTALLATION_STATUS_LABEL[s] || s : "Not started",
      state: s === "COMPLETED" ? "done" : s === "ON_HOLD" ? "warn" : "todo",
    });
  }
  if (order.isCancelled) {
    lifeSteps.push({ key: "cancelled", label: "Cancelled", sub: d(order.cancelledDate), state: "cancel" });
  } else {
    lifeSteps.push({ key: "closed", label: "Closed", sub: order.isClosed ? d(order.closedDate) : null, state: order.isClosed ? "done" : "todo" });
    lifeSteps.push({
      key: "commission", label: "Commission",
      sub: order.isCommissionPaid ? money(order.commissionAmount) : null,
      state: order.isCommissionPaid ? "done" : "todo",
    });
    const next = lifeSteps.findIndex((s) => s.state !== "done");
    if (next >= 0 && lifeSteps[next].state === "todo") lifeSteps[next].state = "current";
  }

  // Installation: Scheduled -> In Progress (or On Hold) -> Completed.
  const inst = order.installationStatus;
  const installSteps = [
    { key: "scheduled", label: "Scheduled", sub: d(order.scheduledDate) || null, state: inst ? "done" : "todo" },
    inst === "ON_HOLD"
      ? { key: "progress", label: "On Hold", sub: d(order.onHoldDate) || null, state: "warn" }
      : {
          key: "progress", label: "In Progress",
          sub: order.startedDate ? `${d(order.startedDate)}${order.expectedEndDate ? ` → ${d(order.expectedEndDate)}` : ""}` : null,
          state: inst === "COMPLETED" ? "done" : inst === "IN_PROGRESS" ? "current" : "todo",
        },
    { key: "completed", label: "Completed", sub: d(order.completedDate) || null, state: inst === "COMPLETED" ? "done" : "todo" },
  ];
  if (inst === "SCHEDULED") installSteps[1].state = "current";

  const openInstallForm = () => {
    setInstallStatus(order.installationStatus || "SCHEDULED");
    setScheduledDate(order.scheduledDate?.slice(0, 10) || "");
    setStartedDate(order.startedDate?.slice(0, 10) || "");
    setExpectedDays(order.expectedDays ? String(order.expectedDays) : "");
    setOnHoldDate(order.onHoldDate?.slice(0, 10) || "");
    setOnHoldRemarks(order.onHoldRemarks || "");
    setCompletedDate(order.completedDate?.slice(0, 10) || "");
    setHandoverNumber(order.handoverNumber || "");
    setTechnicianIds(order.technicianEmployeeIds || "");
    setShowInstallForm(true);
  };

  const handleSaveInstallation = async () => {
    if (installStatus === "COMPLETED" && !completedDate) return toast.error("Completed date is required when status is Completed");
    if (installStatus === "ON_HOLD" && !onHoldRemarks.trim()) return toast.error("On-hold remarks are required when status is On Hold");
    try {
      await setInstallation({
        recordGuid,
        status: installStatus,
        scheduledDate: scheduledDate || undefined,
        startedDate: startedDate || undefined,
        expectedDays: expectedDays ? Number(expectedDays) : undefined,
        onHoldDate: onHoldDate || undefined,
        onHoldRemarks: onHoldRemarks || undefined,
        completedDate: completedDate || undefined,
        handoverNumber: handoverNumber || undefined,
        technicianEmployeeIds: technicianIds || undefined,
      }).unwrap();
      toast.success("Installation updated");
      setShowInstallForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not update installation");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.salesOrders} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Orders</Link>
              <h4>
                {order.orderNumber}{" "}
                <span className={`badge ${orderStatus(order).cls}`}>{orderStatus(order).label}</span>
              </h4>
              <h6>{order.branchCode}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {!order.isClosed && canCloseOrder && (
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
                onClick={() => setShowCancelForm(true)}
                disabled={isCancelling}
                title="The customer backed out - cancel this order"
              >
                <XCircle size={16} /> Cancel Order
              </button>
            )}
            {!order.isClosed && canCloseOrder && (
              <button
                type="button"
                className="btn btn-outline-danger d-inline-flex align-items-center gap-1"
                onClick={() => setShowCloseForm(true)}
                disabled={isClosing || isDeploymentPending}
                title={
                  isDeploymentPending
                    ? "This order still has guards awaiting deployment - deploy them before closing it."
                    : "Close this order"
                }
              >
                <Slash size={16} /> Close Order
              </button>
            )}
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body d-flex align-items-center gap-3">
            <span className="master-detail-avatar master-detail-avatar-lg">{(order.customerName || "?").trim().slice(0, 2).toUpperCase()}</span>
            <div className="flex-grow-1">
              <h5 className="mb-0">{order.customerName}</h5>
              <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                <span className="d-inline-flex align-items-center"><Briefcase size={14} className="me-1" />{order.salespersonName}</span>
                <span className="d-inline-flex align-items-center"><Calendar size={14} className="me-1" />{d(order.createdDate)}</span>
                {order.area && <span className="d-inline-flex align-items-center"><MapPin size={14} className="me-1" />{order.area}</span>}
                <span>Total: <strong>{money(order.totalAmount)}</strong></span>
                {order.guardsCount > 0 && !order.isCancelled && (
                  <span className={`badge ${order.isGuardDeployed ? "badge-linesuccess" : "badge-linewarning"}`}>
                    Guards {order.isGuardDeployed ? "Deployed" : "Pending Deployment"}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Where the order stands, at a glance (2026-09-12). */}
          <div className="card-body border-top pt-3 pb-2">
            <StepTrack steps={lifeSteps} />
          </div>
        </div>

        {/* Say WHY the button is dead rather than leaving a greyed-out
            control and a tooltip nobody hovers. */}
        {!order.isClosed && canCloseOrder && isDeploymentPending && (
          <div className="alert alert-warning d-flex align-items-start">
            <AlertTriangle size={16} className="me-2 mt-1 flex-shrink-0" />
            <div>
              <strong>This order cannot be closed yet.</strong> It has {order.guardsCount} guard
              {order.guardsCount === 1 ? "" : "s"} still awaiting deployment. Mark the guard deployment first, then
              close the order.
            </div>
          </div>
        )}

        {/* Reported 2026-09-21: the screen said who CLOSED an order but never who
            created it. "Created by" is whoever booked the enquiry; "for" is the
            salesperson it was booked for - shown only when that is someone else. */}
        <div className="alert alert-light border py-2">
          Created by <strong>{booking?.bookedByName || "not recorded"}</strong>
          {booking?.bookedDate ? <> on {d(booking.bookedDate)}</> : null}
          {bookedForOther ? <> · booked for <strong>{order.salespersonName}</strong></> : null}
          <span className="text-muted"> · order confirmed {d(order.createdDate)}</span>
        </div>

        {order.isCancelled && (
          <div className="alert alert-secondary d-flex align-items-start">
            <XCircle size={16} className="me-2 mt-1 flex-shrink-0" />
            <div>
              <strong>Cancelled</strong> by {order.cancelledByName || "unknown"} on {d(order.cancelledDate)}
              {order.cancelReason ? ` · ${order.cancelReason}` : ""}
              <div className="small text-muted mt-1">
                Excluded from sales totals, commission and every open-order list. The enquiry stays Confirmed.
              </div>
            </div>
          </div>
        )}

        {order.isClosed && !order.isCancelled && (
          <div className="alert alert-secondary">
            Closed by {order.closedByName} on {d(order.closedDate)}
            {order.closedReason ? ` · Invoice ${order.closedReason}` : ""}
          </div>
        )}

        {/* Commission, closed orders only - the whole point of closing first
            is that the commission is earned on completed business. The
            worklist at /sales/commissions is where these get worked through
            in bulk; this card is the same action in the order's own context.
            Never on a cancelled order (also closed on the wire) - the API
            refuses commission on one. */}
        {order.isClosed && !order.isCancelled && canManageCommission && (
          // Green "Paid" / amber "Due" ribbon card (2026-09-12) - the state
          // reads from the colour before anyone reads the words.
          <div
            className="card mb-3"
            style={{
              borderLeft: `4px solid ${order.isCommissionPaid ? PANEL_ACCENT.money : "#E0A100"}`,
              background: order.isCommissionPaid ? "#F1F8F3" : "#FFFBEB",
              border: `1px solid ${order.isCommissionPaid ? "#CDE8D6" : "#FDE7A9"}`,
            }}
          >
            <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 28, height: 28, background: order.isCommissionPaid ? PANEL_ACCENT.money : "#E0A100", color: "#fff" }}
                  >
                    {order.isCommissionPaid ? <CheckCircle size={15} /> : <Clock size={15} />}
                  </span>
                  <strong>Sales Commission</strong>
                  <span
                    className="badge rounded-pill"
                    style={{ background: order.isCommissionPaid ? PANEL_ACCENT.money : "#E0A100", color: "#fff", fontWeight: 600 }}
                  >
                    {order.isCommissionPaid ? "Paid" : "Due"}
                  </span>
                  {order.isCommissionPaid && (
                    <strong style={{ color: PANEL_ACCENT.money }}>{money(order.commissionAmount)}</strong>
                  )}
                </div>
                <div className="text-muted small mt-1">
                  {order.isCommissionPaid ? (
                    <>
                      {money(order.commissionAmount)} to {order.salespersonName || "the salesperson"} on{" "}
                      {d(order.commissionPaidDate)}
                      {order.commissionChequeNumber ? ` · ${order.commissionChequeNumber}` : ""}
                    </>
                  ) : (
                    <>Not recorded yet for {order.salespersonName || "the salesperson"}.</>
                  )}
                </div>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${order.isCommissionPaid ? "btn-outline-secondary" : "btn-primary"}`}
                onClick={() => setShowCommission(true)}
              >
                {order.isCommissionPaid ? "Edit / Reverse" : "Record Commission"}
              </button>
            </div>
          </div>
        )}

        <CollapsibleCard
          title="Pricing"
          icon={DollarSign}
          accent={PANEL_ACCENT.money}
          hasData={Number(order.totalAmount) > 0}
          forceOpen={showDiscountForm}
          summary={`Total ${money(order.totalAmount)}${order.discountAmount > 0 ? " · discounted" : ""}`}
          actions={
            canEditDiscount && !showDiscountForm && (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={openDiscountForm}>
                <Edit size={14} className="me-1" /> {order.discountAmount > 0 ? "Edit Discount" : "Add Discount"}
              </button>
            )
          }
        >
          {/* The priced lines, or for an older order one tile per priced
              service; then a tinted totals strip with the grand total as the
              loudest figure (2026-09-12 beautification). */}
          {linesArePriced ? (
            <div className="mb-3"><ServiceLinesTable lines={serviceLines} /></div>
          ) : (
            <>
            {serviceTypes.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {serviceTypes.map((s) => (
                  <ServicePill key={s.serviceTypeId} name={s.serviceTypeName} kind={s.serviceKind} />
                ))}
              </div>
            )}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {order.guardsAmount > 0 && (
                <AmountTile label="Guards" amount={order.guardsAmount} color={PANEL_ACCENT.guards}
                  sub={order.guardsRate > 0 ? `${order.guardsCount} × ${money(order.guardsRate)}/guard` : null} />
              )}
              {order.alarmsAmount > 0 && (
                <AmountTile label="Alarms" amount={order.alarmsAmount} color={PANEL_ACCENT.services}
                  sub={order.alarmsRate > 0 ? `${order.alarmsCount} × ${money(order.alarmsRate)}` : null} />
              )}
              {order.otherServicesAmount > 0 && (
                <AmountTile label="Other Services" amount={order.otherServicesAmount} color={PANEL_ACCENT.work} />
              )}
            </div>
            </>
          )}
          <div
            className="d-flex flex-wrap align-items-center gap-4 px-3 py-2 rounded"
            style={{ background: "#EEF7F1", border: "1px solid #CDE8D6" }}
          >
            <span>Sub Total: <strong>{money(orderSubTotal)}</strong></span>
            {order.discountAmount > 0 && (
              <>
                <span className="text-danger">
                  Discount{order.discountPercent ? ` (${Number(order.discountPercent)}%)` : ""}: <strong>-{money(order.discountAmount)}</strong>
                </span>
                <span>Net: <strong>{money(orderSubTotal - order.discountAmount)}</strong></span>
              </>
            )}
            <span>VAT ({Number(order.vatRate) || 0}%): <strong>{money(order.vatAmount)}</strong></span>
            <span className="ms-auto" style={{ color: PANEL_ACCENT.money }}>
              Total (incl. VAT): <strong style={{ fontSize: 18 }}>{money(order.totalAmount)}</strong>
            </span>
          </div>

          {showDiscountForm && (
            <div className="mt-3 p-2 rounded" style={{ background: "#F8F9FA", border: "1px solid #E9ECEF" }}>
              <div className="row align-items-end">
                <DiscountFields
                  percent={discountPercent}
                  amount={discountAmount}
                  onPercentChange={setDiscountPercent}
                  onAmountChange={setDiscountAmount}
                />
                <div className="col-md-6 mb-3 d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-primary" disabled={isSavingDiscount} onClick={handleSaveDiscount}>
                    {isSavingDiscount ? "Saving..." : "Save Discount"}
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" disabled={isSavingDiscount} onClick={() => setShowDiscountForm(false)}>
                    Cancel
                  </button>
                </div>
              </div>
              <DiscountedTotalsStrip totals={newTotals} vatLabel={`VAT (${Number(order.vatRate) || 0}%)`} percent={discountPercent} />
              <small className="text-muted d-block mt-1">
                A percentage or a flat amount, not both - clear both to remove it. VAT is charged on the discounted figure.
                Open orders only; the enquiry keeps the discount it was quoted with.
              </small>
            </div>
          )}
        </CollapsibleCard>

        {/* Source enquiry, quotations, LPOs, stock-outs, technical work and
            guard assignments - each opens the real record (Batch166). */}
        <SalesLinkedDocumentsPanel enquiryRecordGuid={order.enquiryRecordGuid} hideKind="SALES_ORDER" />

        {/* Quotations hang off the order's originating enquiry - the same
            link either screen sees. */}
        <SalesQuotationsPanel
          enquiryRecordGuid={order.enquiryRecordGuid}
          branchId={order.branchId}
          custId={order.custId}
          customerBranchId={order.branchId}
          customerName={order.customerName}
          reference={{ key: `order-${order.recordGuid}`, salesEnquiryRecordGuid: order.enquiryRecordGuid, salesReferenceNumber: order.orderNumber }}
          serviceLines={serviceLines}
          vatRate={order.vatRate}
        />

        <CollapsibleCard
          title="Installation"
          icon={Tool}
          accent={PANEL_ACCENT.work}
          hasData={Boolean(order.installationStatus)}
          forceOpen={showInstallForm}
          summary={order.installationStatus ? INSTALLATION_STATUS_LABEL[order.installationStatus] || order.installationStatus : null}
          actions={
            // The API refuses installation updates on a cancelled order.
            canManageOperations && !order.isCancelled && (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={openInstallForm}>
                <Edit size={14} className="me-1" /> Update
              </button>
            )
          }
        >
          {showInstallForm && (
            <div className="card mb-3">
              <div className="card-body row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={installStatus} onChange={(e) => setInstallStatus(e.target.value)}>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div className="col-md-3 mb-3"><label className="form-label">Scheduled Date</label><input type="date" className="form-control" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} /></div>
                <div className="col-md-3 mb-3"><label className="form-label">Started Date</label><input type="date" className="form-control" value={startedDate} onChange={(e) => setStartedDate(e.target.value)} /></div>
                <div className="col-md-3 mb-3"><label className="form-label">Expected Days</label><input type="number" className="form-control" value={expectedDays} onChange={(e) => setExpectedDays(e.target.value)} /></div>
                {installStatus === "ON_HOLD" && (
                  <>
                    <div className="col-md-3 mb-3"><label className="form-label">On-Hold Date</label><input type="date" className="form-control" value={onHoldDate} onChange={(e) => setOnHoldDate(e.target.value)} /></div>
                    <div className="col-md-9 mb-3"><label className="form-label">On-Hold Remarks (required)</label><input type="text" className="form-control" value={onHoldRemarks} onChange={(e) => setOnHoldRemarks(e.target.value)} /></div>
                  </>
                )}
                {installStatus === "COMPLETED" && (
                  <div className="col-md-3 mb-3"><label className="form-label">Completed Date (required)</label><input type="date" className="form-control" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} /></div>
                )}
                <div className="col-md-3 mb-3"><label className="form-label">Handover Number</label><input type="text" className="form-control" value={handoverNumber} onChange={(e) => setHandoverNumber(e.target.value)} /></div>
                <div className="col-md-6 mb-3"><label className="form-label">Technician(s)</label><input type="text" className="form-control" value={technicianIds} onChange={(e) => setTechnicianIds(e.target.value)} placeholder="Comma-separated employee IDs" /></div>
                <div className="col-12 d-flex gap-2">
                  <button type="button" className="btn btn-primary" onClick={handleSaveInstallation} disabled={isSavingInstallation}>Save</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowInstallForm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {order.installationStatus ? (
            <>
              <StepTrack steps={installSteps} />
              <div className="d-flex flex-wrap gap-3 mt-3 small">
                {order.expectedDays ? <span className="text-muted">Expected: <strong>{order.expectedDays} day{order.expectedDays === 1 ? "" : "s"}</strong></span> : null}
                {order.handoverNumber && <span className="text-muted">Handover No: <strong>{order.handoverNumber}</strong></span>}
                {inst === "ON_HOLD" && order.onHoldRemarks && (
                  <span className="px-2 py-1 rounded" style={{ background: "#FFF8E1", color: "#8D6E00", border: "1px solid #FFE08A" }}>
                    On hold: {order.onHoldRemarks}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="text-muted small">No installation record yet.</div>
          )}
        </CollapsibleCard>

        {/* What the customer was emailed at each installation stage, and their reply.
            Keyed by the order's guid; renders nothing until an email has gone out. */}
        <CustomerFeedbackPanel sourceRecordGuid={recordGuid} />

        {guardRequirements.length > 0 && (
          <CollapsibleCard
            title="Guard Requirements"
            icon={Shield}
            accent={PANEL_ACCENT.guards}
            defaultOpen={false}
            count={guardRequirements.reduce((s, l) => s + (Number(l.qty) || 0), 0)}
            summary={`guard${guardRequirements.reduce((s, l) => s + (Number(l.qty) || 0), 0) === 1 ? "" : "s"} · ${guardRequirements.length} line${guardRequirements.length === 1 ? "" : "s"}`}
          >
            <GuardRequirementsTable lines={guardRequirements} />
          </CollapsibleCard>
        )}
      </div>

      {/* Closing is a decisive, one-way step, so it gets a real modal instead
          of the window.prompt this screen used before - a prompt cannot
          enforce a required field and gives no room to say what the number is
          for. The value is stored in the order's ClosedReason column; only
          the label and the requirement are new. */}
      <Modal
        show={showCloseForm}
        onHide={isClosing ? undefined : () => setShowCloseForm(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Close Order {order.orderNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Closing marks the order as fully delivered and invoiced. It cannot be reopened from here.
          </p>
          <label className="form-label">Invoice Number <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={invoiceNumber}
            placeholder="e.g. INV-2026-00412"
            autoFocus
            onChange={(e) => setInvoiceNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && invoiceNumber.trim()) handleClose();
            }}
          />
          <small className="text-muted">Recorded against the order and shown on its closed banner.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloseForm(false)} disabled={isClosing}>Cancel</Button>
          <Button variant="danger" onClick={handleClose} disabled={isClosing || !invoiceNumber.trim()}>
            {isClosing ? "Closing..." : "Close Order"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCancelForm}
        onHide={isCancelling ? undefined : () => setShowCancelForm(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order {order.orderNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Use this when the customer has backed out. The order leaves every open list, Pending Deployments
            and sales totals, and any unfinished technical work raised from it is closed. It cannot be undone
            from here. (For a delivered and invoiced order, use Close Order instead.)
          </p>
          <label className="form-label">Reason <span className="text-danger">*</span></label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={300}
            value={cancelReason}
            autoFocus
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <small className="text-muted">{cancelReason.length}/300</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelForm(false)} disabled={isCancelling}>Keep Order</Button>
          <Button variant="danger" onClick={handleCancel} disabled={isCancelling || !cancelReason.trim()}>
            {isCancelling ? "Cancelling..." : "Cancel Order"}
          </Button>
        </Modal.Footer>
      </Modal>

      <CommissionModal show={showCommission} order={order} onClose={() => setShowCommission(false)} />
    </div>
  );
};

export default OrderDetail;
